import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { validateBirthday } from '@frontend/api-weave-pay';
import { http } from '@frontend/fetch';
import { Trans, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  Text,
  useFormField,
  DateField,
  useAlert,
  Button,
  ContentLoader,
  Heading,
  ButtonBar,
} from '@frontend/design-system';
import EncryptIcon from './assets/encrypt.png';
import HippaIcon from './assets/hippa.png';

const styles = {
  formContainer: css`
    border: 1px solid ${theme.colors.neutral20};
    box-shadow: ${theme.shadows.light};
    border-radius: ${theme.borderRadius.small};
    background-color: ${theme.colors.white};
    padding: 0 ${theme.spacing(3)};
    width: 500px;
    height: min-content;
  `,
  securityBadges: css`
    margin-top: ${theme.spacing(3)};
    display: flex;
    align-items: center;
    .encryptIcon {
      height: 40px;
      margin-right: 24px;
      margin-top: 6px;
    }
    .hippaIcon {
      height: 30px;
    }
  `,
  birthdate: css`
    margin-top: ${theme.spacing(3)};
  `,
  buttonBar: css`
    justify-content: space-between;
  `,
};

interface Props {
  attachmentLink: string;
  personName?: string;
  onCancel?: () => void;
  onSuccess?: () => void;
}

export const AttachmentAuth = ({ attachmentLink, personName, onCancel, onSuccess }: Props) => {
  const { t } = useTranslation('weavepay');
  const alerts = useAlert();
  const [submitReady, setSubmitReady] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const dateFieldProps = useFormField({
    required: true,
    minDate: '12/31/1899',
    maxDate: new Date().toLocaleDateString(),
    type: 'date',
  });

  const checkBirthdayValidity = async () => {
    setIsVerifying(true);
    try {
      await validateBirthday(attachmentLink, dateFieldProps.value);
    } catch (err) {
      if (http.isHttpError(err) && err.status === 401) {
        alerts.error({
          autoDismissAfter: 12000,
          message: t('Birthday did not match our records.'),
        });
        setSubmitReady(false);
      } else {
        setSubmitReady(true);
      }
    } finally {
      setIsVerifying(false);
    }
  };

  useEffect(() => {
    if (!dateFieldProps.error) {
      checkBirthdayValidity();
    } else {
      setSubmitReady(false);
    }
  }, [dateFieldProps.value]);

  const onOpen = async () => {
    try {
      await validateBirthday(attachmentLink, dateFieldProps.value);
    } catch (err) {
      alerts.error({
        autoDismissAfter: 12000,
        message: t('We were unable to open the attachment, please try again.'),
      });
    } finally {
      onSuccess?.();
    }
  };

  if (isVerifying) return <ContentLoader message={t('Authenticating birthday')} />;

  return (
    <div css={styles.formContainer}>
      <form method='post' action={attachmentLink} onSubmit={onOpen} target='_blank'>
        <Heading level={3}>{t('Authentication Required')}</Heading>
        <Text size={'large'}>
          <Trans t={t}>
            This payment request includes a secure billing attachment. To view the attachment, please authenticate by
            filling in the information below.
          </Trans>
        </Text>

        <div css={styles.birthdate}>
          <Text>{t('Birthday for:')}</Text>
          <Text weight='bold'>{personName || t('Yourself')}</Text>
          <DateField {...dateFieldProps} name='birthday' label={t('Birthday')} />
        </div>

        <div css={styles.securityBadges}>
          <img src={EncryptIcon} className='encryptIcon' alt='Secure' />
          <img src={HippaIcon} className='hippaIcon' alt='HIPPA Compliant' />
        </div>

        <ButtonBar css={styles.buttonBar} removeHorizontalSpacing>
          <Button variant='tertiary' onClick={onCancel}>
            {t('Back')}
          </Button>
          <Button type='submit' disabled={!submitReady}>
            {t('Open Invoice')}
          </Button>
        </ButtonBar>
      </form>
    </div>
  );
};
